import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdminUsers, AdminUsersSortBy } from '../../hooks/useAdminUsers';
import { getAdminUserDetails, type AdminUserDetails } from '../../api/admin.api';
import './admin.css';


function maskPhone(phone: string | null): string {
  if (!phone || !phone.trim()) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
}


function UserDetailsPanel({
  open,
  user,
  loading,
  onClose,
}: {
  open: boolean;
  user: AdminUserDetails | null;
  loading: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <>
      <button type="button" className="admin-user-drawer-backdrop" onClick={onClose} aria-label="Закрыть детали пользователя" />
      <aside className="admin-user-drawer">
        <div className="admin-section-header">
          <h3 className="admin-card-title">Детали пользователя</h3>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={onClose}>Закрыть</button>
        </div>
        {loading && <div className="admin-field-hint">Загрузка...</div>}
        {!loading && !user && <div className="admin-field-hint">Не удалось загрузить детали пользователя.</div>}
        {!loading && user && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="admin-card">
              <div><strong>{user.name || 'Без имени'}</strong></div>
              <div>Телефон: {user.phone || '—'}</div>
              <div>Telegram ID: {user.telegramId}</div>
              <div>Username: {user.telegramUsername ? `@${user.telegramUsername}` : '—'}</div>
              <div>Источник: {user.source || '—'}</div>
              <div>Регистрация: {new Date(user.createdAt).toLocaleString('ru-RU')}</div>
            </div>
            <div className="admin-card">
              <div>Заказов: {user.orderCount}</div>
              <div>Оплаченных: {user.paidOrderCount}</div>
              <div>Сумма: {user.totalSum.toLocaleString('ru-RU')} ₽</div>
              <div>Оплачено: {user.paidTotalSum.toLocaleString('ru-RU')} ₽</div>
              <div>Бонусы: {user.bonusBalance.toLocaleString('ru-RU')}</div>
              <div>Последний заказ: {user.lastOrderAt ? new Date(user.lastOrderAt).toLocaleString('ru-RU') : '—'}</div>
              <Link className="admin-link" to={`/admin/audit?q=${encodeURIComponent(user.telegramId)}`}>
                Поиск в журнале по Telegram ID
              </Link>
            </div>
            <div className="admin-card">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Последние заказы</div>
              {user.recentOrders.length === 0 ? (
                <div className="admin-field-hint">Заказов пока нет.</div>
              ) : (
                user.recentOrders.map((order) => (
                  <div key={order.id} className="admin-user-order-item">
                    <span>#{order.id}</span>
                    <span>{order.total.toLocaleString('ru-RU')} ₽</span>
                    <span>{order.status || '—'}/{order.paymentStatus || '—'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

function TableSkeleton() {
  return (
    <div className="admin-skeleton-table">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div key={i} className="admin-skeleton-row">
          <div className="admin-skeleton-cell" style={{ width: '14%' }} />
          <div className="admin-skeleton-cell" style={{ width: '12%' }} />
          <div className="admin-skeleton-cell" style={{ width: '12%' }} />
          <div className="admin-skeleton-cell" style={{ width: '12%' }} />
          <div className="admin-skeleton-cell" style={{ width: '10%' }} />
          <div className="admin-skeleton-cell" style={{ width: '10%' }} />
          <div className="admin-skeleton-cell" style={{ width: '12%' }} />
          <div className="admin-skeleton-cell" style={{ width: '10%' }} />
        </div>
      ))}
    </div>
  );
}

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    users,
    pagination,
    loading,
    error,
    from,
    to,
    source,
    search,
    page,
    sortBy,
    sortOrder,
    setFrom,
    setTo,
    setSource,
    setSearch,
    setPage,
    setSort,
    resetFilters,
  } = useAdminUsers();
  const [compactView, setCompactView] = useState<boolean>(() => window.innerWidth <= 900);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState<AdminUserDetails | null>(null);
  const detailsRequestSeq = useRef(0);
  const deepLinkedUserId = searchParams.get('userId');
  useEffect(() => {
    const onResize = () => setCompactView(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const hasFilters = Boolean(from || to || source || search);
  const empty = !loading && users.length === 0;

  const handleSort = (key: AdminUsersSortBy) => {
    const nextOrder = sortBy === key && sortOrder === 'desc' ? 'asc' : 'desc';
    setSort(key, nextOrder);
  };
  const openDetails = async (id: number) => {
    if (detailsLoading && details?.id === id) return;
    const reqId = ++detailsRequestSeq.current;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('userId', String(id));
      return next;
    });
    try {
      const data = await getAdminUserDetails(id);
      if (reqId !== detailsRequestSeq.current) return;
      setDetails(data);
    } catch {
      if (reqId !== detailsRequestSeq.current) return;
      setDetails(null);
    } finally {
      if (reqId === detailsRequestSeq.current) setDetailsLoading(false);
    }
  };
  useEffect(() => {
    const id = Number(deepLinkedUserId);
    if (!deepLinkedUserId || !Number.isInteger(id) || id <= 0) return;
    if (details?.id === id && detailsOpen) return;
    void openDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedUserId]);

  return (
    <div className="admin-page">
      <div className="admin-filters admin-filters-sticky">
        <input
          type="date"
          placeholder="От"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="admin-input admin-input-date"
          data-testid="admin-users-filter-from"
        />
        <input
          type="date"
          placeholder="До"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="admin-input admin-input-date"
          data-testid="admin-users-filter-to"
        />
        <input
          type="text"
          placeholder="Источник"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="admin-input admin-input-source"
          data-testid="admin-users-filter-source"
        />
        <input
          type="text"
          placeholder="Поиск: телефон, @username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-input admin-input-search"
          data-testid="admin-users-filter-search"
        />
        <button type="button" onClick={resetFilters} className="admin-btn admin-btn-secondary" disabled={!hasFilters} title="Сбросить фильтры">
          Сбросить
        </button>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {loading && !users.length ? (
        <TableSkeleton />
      ) : empty ? (
        <div className="admin-empty">
          <p>Нет пользователей по заданным фильтрам</p>
          <p className="admin-empty-hint">Измените период, источник или поиск, либо сбросьте фильтры.</p>
          <button type="button" onClick={resetFilters} className="admin-btn admin-btn-primary">
            Сбросить фильтры
          </button>
        </div>
      ) : compactView ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {users.map((u) => (
            <div key={u.id} className="admin-card">
              <div><strong>{u.name ?? '—'}</strong></div>
              <div>Телефон: {maskPhone(u.phone)}</div>
              <div>Telegram: {u.telegramUsername ? `@${u.telegramUsername}` : '—'}</div>
              <div>ID: {u.telegramId}</div>
              <div>Регистрация: {u.createdAt.slice(0, 10)}</div>
              <div>Заказов: {u.orderCount}</div>
              <div>Сумма: {u.totalSum.toLocaleString('ru-RU')} ₽</div>
              <div>Бонусы: {u.bonusBalance.toLocaleString('ru-RU')}</div>
              <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" data-testid={`admin-users-open-${u.id}`} onClick={() => openDetails(u.id)}>Подробнее</button>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-th">ФИО</th>
                  <th className="admin-th">Телефон</th>
                  <th className="admin-th">Telegram</th>
                  <th className="admin-th">telegram_id</th>
                  <th className="admin-th admin-th-sort" onClick={() => handleSort('created_at')}>
                    Регистрация {sortBy === 'created_at' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="admin-th admin-th-sort" onClick={() => handleSort('order_count')}>
                    Заказов {sortBy === 'order_count' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="admin-th admin-th-sort" onClick={() => handleSort('total_sum')}>
                    Сумма {sortBy === 'total_sum' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="admin-th admin-th-sort" onClick={() => handleSort('bonus_balance')}>
                    Бонусы {sortBy === 'bonus_balance' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="admin-th admin-center">Детали</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                      <tr key={u.id} className="admin-table-row">
                        <td className="admin-td">{u.name ?? '—'}</td>
                        <td className="admin-td">{maskPhone(u.phone)}</td>
                        <td className="admin-td">{u.telegramUsername ? `@${u.telegramUsername}` : '—'}</td>
                        <td className="admin-td admin-mono">{u.telegramId}</td>
                        <td className="admin-td">{u.createdAt.slice(0, 10)}</td>
                        <td className="admin-td admin-num">{u.orderCount}</td>
                        <td className="admin-td admin-num">{u.totalSum.toLocaleString('ru-RU')} ₽</td>
                        <td className="admin-td admin-num">{u.bonusBalance.toLocaleString('ru-RU')}</td>
                        <td className="admin-td admin-center">
                          <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" data-testid={`admin-users-open-${u.id}`} onClick={() => openDetails(u.id)}>Открыть</button>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="admin-pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="admin-btn admin-btn-paginate"
              >
                Назад
              </button>
              <span className="admin-pagination-info">
                {page} / {pagination.totalPages} (всего {pagination.total})
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage(page + 1)}
                className="admin-btn admin-btn-paginate"
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}
      <UserDetailsPanel
        open={detailsOpen}
        user={details}
        loading={detailsLoading}
        onClose={() => {
          setDetailsOpen(false);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('userId');
            return next;
          });
        }}
      />
    </div>
  );
}
